/**
 * 招募模式 的進出轉場。
 *
 * 一道由開關本身放射出去的色浪掃過整頁,掃到七成時換主題,四張統計卡再逐張
 * 轉正。兩段疊在一起,所以換色那一刻被色浪蓋住 —— 看起來是「掃出來的」,而不是
 * 「閃了一下」。
 *
 * 🔒 慢。整段約 2.4 秒,是使用者選的 0.35 倍速。一個一天可能來回切幾次的開關,
 * 這個長度是刻意的排場;覺得長的話,改 SPEED 一個數字就夠,底下所有時間都是從
 * 它算出來的,不必重新編排。
 *
 * 唯一的例外是系統層的「減少動態效果」。開了它的人直接切換,零動畫 —— 這頁本來
 * 就到處尊重這個設定(見 StatTiles 的 useCountUp)。
 */

/** 0.35× —— 所有時間的來源。 */
export const RECRUIT_TRANSITION_SPEED = 0.35;

const BASE_WIPE_MS = 380;
const BASE_FLIP_MS = 460;
const BASE_STAGGER_MS = 60;

export const RECRUIT_WIPE_MS = Math.round(BASE_WIPE_MS / RECRUIT_TRANSITION_SPEED);
export const RECRUIT_FLIP_MS = Math.round(BASE_FLIP_MS / RECRUIT_TRANSITION_SPEED);
export const RECRUIT_STAGGER_MS = Math.round(BASE_STAGGER_MS / RECRUIT_TRANSITION_SPEED);

/**
 * 色浪掃到七成時換主題。整段掃完才換,換色會發生在一片乾淨的畫面上,等於閃一
 * 下;七成時換,新顏色是從色浪底下露出來的。
 */
export const RECRUIT_APPLY_AT_MS = Math.round(RECRUIT_WIPE_MS * 0.7);

/** 最後一張卡轉完為止。 */
export const RECRUIT_TRANSITION_MS = RECRUIT_APPLY_AT_MS + RECRUIT_FLIP_MS + RECRUIT_STAGGER_MS * 3;

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 一下輕震。
 *
 * ⚠️ 用的是 Web Vibration API,不是 Capacitor 的 Haptics —— 後者沒有裝
 * (package.json 裡沒有 @capacitor/haptics),而加一個原生外掛需要 cap sync 與
 * 一次原生重建,不是一個純前端 PR 該夾帶的東西。
 *
 * 代價說清楚:Android 的 WebView 有這個 API,iOS 的 WKWebView 沒有,所以 iPhone
 * 上這一下是靜的。要 iOS 也震,得先裝那個外掛。
 *
 * 任何一步失敗都當作沒有發生 —— 一個開關不會因為震不動而不開。
 */
export const pulse = (): void => {
  if (prefersReducedMotion()) return;
  try {
    navigator.vibrate?.(12);
  } catch (error) {
    // 有些瀏覽器在沒有使用者手勢的情況下會丟例外,而這不值得一個壞掉的開關。
  }
};

export type WipeOrigin = { x: number; y: number };

/**
 * 找出色浪該從哪裡放射。就是按下去的那顆開關 —— 進去是分頁列上的「招募」晶片,
 * 出來是分頁列上的「業務」晶片(橫幅已經拿掉了)。
 *
 * 用 DOM 查而不是把事件一路傳下來:為了一個純
 * 裝飾的座標把 event 穿過三層 props,是讓每一層都知道一件它不需要知道的事。
 * 找不到就回中間 —— 位置錯了只是動畫沒那麼貼,不該是個錯誤。
 */
export const wipeOriginOf = (page: HTMLElement | null): WipeOrigin | null => {
  if (!page || typeof document === 'undefined') return null;
  const pageRect = page.getBoundingClientRect();
  const switchEl =
    document.querySelector('[data-testid="client-tab-business"]') ||
    document.querySelector('[data-testid="client-tab"][data-tab="recruit"]');
  if (!switchEl) return { x: pageRect.width / 2, y: pageRect.height / 2 };
  const rect = switchEl.getBoundingClientRect();
  return {
    x: rect.left - pageRect.left + rect.width / 2,
    y: rect.top - pageRect.top + rect.height / 2
  };
};
